import React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Box,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  EmptyHint,
  StatTile,
  Widget,
  WidgetBarChart,
  WidgetDonutChart,
} from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import {
  ExpenseAggregate,
  ExpenseAggregateAxis,
  aggregateExpense,
} from 'stores/ExpenseStoreAdapter';
import { mergeWithDefaults, resolveOption } from 'utils/UserProfileGuard';

import ExpensePeriodSelector, {
  DEFAULT_DATE_RANGE,
  ExpenseDateRange,
  PERIOD_PRESET_KEYS,
  resolveDateRange,
} from './components/ExpensePeriodSelector';

const ACCENT = '#ef6c00';

const AXIS_KEYS: ExpenseAggregateAxis[] = ['LABEL', 'STORE', 'MONTH', 'YEAR'];

type DashboardState = {
  dateRange: ExpenseDateRange;
  axis: ExpenseAggregateAxis;
};

const DEFAULTS: DashboardState = {
  dateRange: DEFAULT_DATE_RANGE,
  axis: 'LABEL',
};

const yen = (n: number) => `${n.toLocaleString('ja-JP')} 円`;

/**
 * 経費ダッシュボード（詳細）。
 * 期間セレクタ（共通）＋ 軸トグル（品目ラベル別/店舗別/月別/年別）＋ 集計グラフ＋ 集計テーブル。
 * グラフのバー/スライスクリックで経費一覧へ絞り込み遷移（ドリルダウン）。
 * 期間・軸は user_profile('expense-dashboard') に保存し Home と共有する。
 */
const ExpenseDashboard: React.FC = () => {
  const navigate = useNavigate();

  // マスタ設定（品目ラベル/店舗）へのショートカット（Home と同じヒーロー内メニュー）
  const [settingsAnchor, setSettingsAnchor] = React.useState<HTMLElement | null>(null);
  const settingsItems = [
    {
      key: 'label',
      label: '品目ラベル管理',
      icon: <CategoryIcon fontSize="small" />,
      to: '/expense/expense-label',
    },
    {
      key: 'store',
      label: '店舗管理',
      icon: <StorefrontIcon fontSize="small" />,
      to: '/expense/expense-store',
    },
  ];

  const [rawState, setState, restored] = useDashboardProfile<DashboardState>(
    'expense-dashboard',
    DEFAULTS,
  );

  // 復元ガード: dateRange.preset / axis を妥当な選択肢へフォールバック
  const state = React.useMemo<DashboardState>(() => {
    const merged = mergeWithDefaults(DEFAULTS, rawState);
    return {
      dateRange: {
        ...merged.dateRange,
        preset: resolveOption(merged.dateRange.preset, PERIOD_PRESET_KEYS, 'THIS_MONTH'),
      },
      axis: resolveOption(merged.axis, AXIS_KEYS, 'LABEL'),
    };
  }, [rawState]);

  const [aggregate, setAggregate] = React.useState<ExpenseAggregate | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  const period = React.useMemo(() => resolveDateRange(state.dateRange), [state.dateRange]);

  const setDateRange = (dateRange: ExpenseDateRange) =>
    setState((prev) => ({ ...prev, dateRange }));
  const setAxis = (_: React.MouseEvent<HTMLElement>, axis: ExpenseAggregateAxis | null) => {
    if (axis) setState((prev) => ({ ...prev, axis }));
  };

  // 集計を取得する。初回ロードと更新ボタンで共用する。
  const reload = React.useCallback(async () => {
    const res = await aggregateExpense({
      axis: state.axis,
      ...(period.dateFrom ? { dateFrom: period.dateFrom } : {}),
      ...(period.dateTo ? { dateTo: period.dateTo } : {}),
    }).catch((): ExpenseAggregate | null => null);
    setAggregate(res);
  }, [state.axis, period.dateFrom, period.dateTo]);

  React.useEffect(() => {
    if (!restored) return;
    let active = true;
    setLoading(true);
    reload().finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [restored, reload]);

  // ヒーローの更新ボタン: 再取得中はスピナー表示にする
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  const groups = React.useMemo(() => aggregate?.groups ?? [], [aggregate]);
  const totalAmount = aggregate?.totalAmount ?? 0;
  const totalCount = aggregate?.totalCount ?? 0;

  // グラフ用データ（key→group 逆引きでドリルダウン）
  const groupByLabel = React.useMemo(() => {
    const m = new Map<string, (typeof groups)[number]>();
    groups.forEach((g) => m.set(g.label, g));
    return m;
  }, [groups]);

  /** 現在の集計期間を引き継いで経費一覧へ遷移する。 */
  const goToExpenses = React.useCallback(() => {
    const params = new URLSearchParams();
    if (period.dateFrom) params.set('dateFrom', period.dateFrom);
    if (period.dateTo) params.set('dateTo', period.dateTo);
    const qs = params.toString();
    navigate(`/expense/expense-management${qs ? `?${qs}` : ''}`);
  }, [navigate, period.dateFrom, period.dateTo]);

  /** 軸値でドリルダウン: 経費一覧へクエリで絞り込み遷移 */
  const drilldown = (label: string) => {
    const g = groupByLabel.get(label);
    if (!g) return;
    const params = new URLSearchParams();
    if (period.dateFrom) params.set('dateFrom', period.dateFrom);
    if (period.dateTo) params.set('dateTo', period.dateTo);
    if (state.axis === 'LABEL') {
      params.set('labelIds', g.key);
    } else if (state.axis === 'STORE') {
      params.set('storeIds', g.key);
    } else if (state.axis === 'MONTH') {
      // key=YYYY-MM → その月の期間で絞り込む
      params.set('dateFrom', `${g.key}-01`);
      const end = new Date(Number(g.key.slice(0, 4)), Number(g.key.slice(5, 7)), 0);
      params.set('dateTo', `${g.key}-${String(end.getDate()).padStart(2, '0')}`);
    } else if (state.axis === 'YEAR') {
      params.set('dateFrom', `${g.key}-01-01`);
      params.set('dateTo', `${g.key}-12-31`);
    }
    navigate(`/expense/expense-management?${params.toString()}`);
  };

  const barData = groups.map((g) => ({ label: g.label, value: g.totalAmount }));
  const donutData = groups.map((g) => ({
    label: g.label,
    value: g.totalAmount,
    color: g.color,
  }));

  // 構成比（DONUT）は品目ラベル別のみ色付き。それ以外は棒グラフ（降順）。
  const useDonut = state.axis === 'LABEL';

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        <DomainHero
          eyebrow="Expense Analytics"
          title="Slice Your Spending"
          tagline="Break down your spending by label, store, month, or year, and drill down to the details."
          palette={HERO_PALETTES.expense}
          onBack={() => navigate('/expense')}
          backLabel="戻る"
          backIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          actions={
            <Stack direction="column" spacing={1} alignItems="flex-end">
              <Tooltip title="マスタ設定">
                <IconButton
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    border: 1,
                    borderColor: 'rgba(255,255,255,0.35)',
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={settingsAnchor}
                open={Boolean(settingsAnchor)}
                onClose={() => setSettingsAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      mt: 0.5,
                      minWidth: 200,
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                    },
                  },
                }}
              >
                {settingsItems.map((it) => (
                  <MenuItem
                    key={it.key}
                    onClick={() => {
                      setSettingsAnchor(null);
                      navigate(it.to);
                    }}
                  >
                    <ListItemIcon sx={{ color: 'text.secondary' }}>{it.icon}</ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                      {it.label}
                    </ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          }
        />

        {/* 期間セレクタ（共通） */}
        <ExpensePeriodSelector value={state.dateRange} onChange={setDateRange} />

        {/* KPI */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<Box component="span">¥</Box>}
              label="合計金額"
              value={totalAmount.toLocaleString('ja-JP')}
              suffix="円"
              color={ACCENT}
              loading={loading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<Box component="span">#</Box>}
              label="件数"
              value={totalCount}
              suffix="件"
              color="#1565c0"
              loading={loading}
              onClick={goToExpenses}
            />
          </Grid>
        </Grid>

        {/* 軸トグル */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={state.axis}
          onChange={setAxis}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 1.75,
            },
          }}
        >
          {AXIS_KEYS.map((k) => (
            <ToggleButton key={k} value={k}>
              {ExpenseAggregateAxis[k]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* 集計グラフ */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: useDonut ? 6 : 12 }}>
            <Widget title={`${ExpenseAggregateAxis[state.axis]}の集計`} fit="content">
              {loading ? (
                <Skeleton variant="rounded" height={260} />
              ) : useDonut ? (
                <WidgetDonutChart
                  data={donutData}
                  totalLabel="合計"
                  unit="円"
                  onSliceClick={drilldown}
                  emptyText="対象期間の経費がありません"
                />
              ) : (
                <WidgetBarChart
                  data={barData}
                  color={ACCENT}
                  onBarClick={drilldown}
                  emptyText="対象期間の経費がありません"
                />
              )}
            </Widget>
          </Grid>

          {/* 集計テーブル */}
          <Grid size={{ xs: 12, md: useDonut ? 6 : 12 }}>
            <Widget title="内訳" fit="content">
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={36} />
                  ))}
                </Stack>
              ) : groups.length === 0 ? (
                <EmptyHint text="対象期間の経費がありません" />
              ) : (
                <Stack spacing={0.5}>
                  {[...groups]
                    .sort((a, b) => b.totalAmount - a.totalAmount)
                    .map((g) => {
                      const ratio =
                        totalAmount > 0 ? Math.round((g.totalAmount / totalAmount) * 100) : 0;
                      return (
                        <Stack
                          key={g.key}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          onClick={() => drilldown(g.label)}
                          sx={{
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            cursor: 'pointer',
                            transition: 'background-color 0.15s',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ minWidth: 0 }}
                          >
                            {g.color && (
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: g.color,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                              {g.label}
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="baseline" spacing={1.5}>
                            <Typography variant="caption" color="text.secondary">
                              {g.count}件 / {ratio}%
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ minWidth: 90, textAlign: 'right' }}
                            >
                              {yen(g.totalAmount)}
                            </Typography>
                          </Stack>
                        </Stack>
                      );
                    })}
                </Stack>
              )}
            </Widget>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default ExpenseDashboard;
